import moment from 'moment';
import React, { useEffect, useState, useRef, useContext } from 'react';
import { Container, Row, Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Modal, Button, Select, Breadcrumb, BreadcrumbItem, TextInput, Spinner } from '../../components';
import { AbilityContext } from '../../shared/abilities/AbilityContext';
import { DocFileReadDto } from '../model/AdminDocument/DocFileReadDto';
import { AdminDocumentSearchResponseDto, DocFolderSearchResultDto, DocFileSearchResultDto } from '../model/AdminDocument/AdminDocumentSearchDto';
import AdminDocumentService from './AdminDocumentService';
import DocViewer from '../shared/components/DocViewer';
import { Column } from '../../components';
import Grid from '../../components/components/Grid/Grid';
import './AdminDocumentViewer.scss';
import { toast } from 'react-toastify';
import Logger from '../../shared/services/Logger';
import * as constant from '../../shared/util/constant';
import {
    ERROR_DOWNLOADING_DOCUMENT,
    UNSUPPORTED_FILE_FORMAT,
    CANCEL,
    DOWNLOAD,
    ALLOWED_UPLOAD_EXTENSIONS,
    UPLOAD_ACCEPT_ATTRIBUTE,
    ALLOWED_UPLOAD_FILE_TYPES_LABEL,
    DOC_NODE_TYPE_CATEGORY,
    DOC_NODE_TYPE_FOLDER,
    MAX_UPLOAD_FILE_SIZE_BYTES,
    MAX_UPLOAD_FILE_SIZE_MB,
    OUTLOOK_MSG_MIME_TYPE,
    UPLOAD_DOCUMENT,
    UPLOAD_FILES_TO,
    DESTINATION,
    DOCUMENT_TYPE,
    DOCUMENT_TYPE_REQUIRED,
    UPLOAD_PROCESSING_TITLE,
    UPLOAD_COMPLETE_TITLE,
    UPLOAD_PROCESSING_MESSAGE,
    ADD_SUB_FOLDER,
    FOLDER_NAME,
    FOLDER_NAME_PLACEHOLDER,
    FOLDER_NAME_REQUIRED,
    FOLDER_NAME_EXISTS,
    FOLDER_NAME_MIN_LENGTH,
    FOLDER_NAME_MAX_LENGTH,
    FOLDER_NAME_TOO_SHORT,
    FOLDER_NAME_TOO_LONG,
    FOLDER_CREATED_SUCCESS,
    FOLDER_CREATION_ERROR,
    CREATE,
    RENAME_SUB_FOLDER,
    CURRENT_NAME,
    NEW_NAME,
    SAVE,
    FOLDER_RENAMED_SUCCESS,
    FOLDER_RENAME_ERROR,
    FOLDER_NOT_FOUND_ERROR,
    FOLDER_NAME_FORBIDDEN_CHARACTERS_PATTERN,
    FOLDER_NAME_FORBIDDEN_CHARACTERS_MESSAGE,
    FOLDER_NAME_TRAILING_CHARACTERS_PATTERN,
    FOLDER_NAME_TRAILING_CHARACTERS_MESSAGE,
    DELETING,
    DOCUMENT_ACTIONS,
    ROW_ACTION_RENAME,
    ROW_ACTION_REPLACE,
    ROW_ACTION_DELETE,
    ROW_ACTION_COMING_SOON,
    DELETE_DOCUMENT_TITLE,
    DELETE_DOCUMENT_WARNING,
    DELETE_DOCUMENT_SUCCESS,
    DELETE_DOCUMENT_ERROR,
    DELETE_FOLDER_TITLE,
    DELETE_FOLDER_PROMPT,
    DELETE_FOLDER_NOT_EMPTY,
    DELETE_FOLDER_ALSO_REMOVES,
    DELETE_FOLDER_DOCUMENTS_LABEL,
    FOLDER_ACTION_ADD,
    DELETE_FOLDER_CONFIRM_CHECKBOX,
    DELETE_FOLDER_WARNING,
    DELETE_FOLDER_COUNT_ERROR,
    DELETE_FOLDER_SUCCESS,
    DELETE_FOLDER_ERROR,
    ADD_NOTE_ERROR,
    ADD_VIEW_NOTES,
    NEW_NOTE_LABEL,
    NEW_NOTE_PLACEHOLDER,
    SUBMIT,
    ADMIN_DOCUMENT_BREADCRUMBS,
    ADMIN_DOCUMENT_SEARCH_PLACEHOLDER,
    SEARCH_MIN_LENGTH,
    SEARCH_MIN_LENGTH_MESSAGE,
    SEARCH_ALLOWED_PATTERN,
    SEARCH_DISALLOWED_CHARACTERS_PATTERN,
    SEARCH_INVALID_CHARACTERS_MESSAGE,
    SEARCH_RESULTS_TITLE,
    SEARCH_FOLDERS_LABEL,
    SEARCH_DOCUMENTS_LABEL,
    SEARCH_NO_RESULTS,
    SEARCH_ERROR,
    SEARCH_IN_PROGRESS,
    CLEAR_SEARCH,
    SEARCH_RESULTS_FOR,
    SEARCH_TAB_ALL,
    DOCUMENT_GRID_DEFAULT_PAGE_SIZE,
    FOLDER_FILTER_PLACEHOLDER,
    DOCUMENT_FILTER_PLACEHOLDER,
    DOCUMENT_FILTER_NO_DOCUMENTS,
    DOCUMENT_FILTER_NO_RESULTS,
    FOLDER_FILTER_NO_RESULTS,
    PREVIEWABLE_DOCUMENT_EXTENSIONS,
    MAX_PREVIEW_SIZE_BYTES,
    UNSUPPORTED_FILE_FORMAT_MESSAGE,
    PREVIEW_UNAVAILABLE_TITLE,
    PREVIEW_TOO_LARGE_MESSAGE,
    PREVIEW_FAILED_MESSAGE
} from '../shared/util/docuMateConstant';
import { CONFIRM_YES, CONFIRM_NO } from '../shared/util/docuMateLabel';
import { downloadFile } from '../../shared/util/DataHelpers';
import { UploadCustomerDocument } from '../services/DocuMateAPI';
import { FaFileAlt, FaChevronRight, FaChevronDown, FaUpload, FaTimes, FaCloudUploadAlt, FaEllipsisV, FaPlus, FaRegEdit, FaExchangeAlt, FaRegTrashAlt } from 'react-icons/fa';
import { FaRegFolder, FaRegFolderOpen, FaSearch } from 'react-icons/fa';
import BulkProgressModal from '../shared/components/BulkProgressModal';
import { NoteDto } from '../model/AdminDocument/NoteDto';

interface SelectedDocument {
    node: DocFileReadDto;
    blob: Blob;
    fileName: string;
}

interface TreeNode {
    id: string;
    name: string;
    appId: number;
    type: typeof DOC_NODE_TYPE_CATEGORY | typeof DOC_NODE_TYPE_FOLDER;
    children: TreeNode[];
    documents: DocFileReadDto[];
    isExpanded: boolean;
    isLoaded: boolean;
}

interface FilteredFolderNode {
    id: string;
    name: string;
    nodeIds: string[];
    isMatch: boolean;
    isExpanded: boolean;
    children: FilteredFolderNode[];
}

const toggleFilteredNode = (nodes: FilteredFolderNode[], key: string): FilteredFolderNode[] =>
    nodes.map(n => n.nodeIds.join('/') === key
        ? { ...n, isExpanded: !n.isExpanded }
        : { ...n, children: toggleFilteredNode(n.children, key) });

// Merges the root-to-leaf pathSegments of each matched folder into a single tree.
const buildFilteredFolderTree = (folders: DocFolderSearchResultDto[], treeNodes: TreeNode[]): FilteredFolderNode[] => {
    const roots: FilteredFolderNode[] = [];
    folders.forEach(folder => {
        const segments = (folder.pathSegments ?? []).filter(s => Boolean(s.id));
        if (segments[segments.length - 1]?.id !== folder.id) {
            segments.push({ id: folder.id, name: folder.name });
        }
        let level = roots;
        const nodeIds: string[] = [];
        segments.forEach((segment, index) => {
            nodeIds.push(segment.id);
            let node = level.find(n => n.id === segment.id);
            if (!node) {
                node = {
                    id: segment.id,
                    name: segment.name ?? findNodeById(treeNodes, segment.id)?.name ?? '',
                    nodeIds: [...nodeIds],
                    isMatch: false,
                    isExpanded: true,
                    children: []
                };
                level.push(node);
            }
            if (index === segments.length - 1) node.isMatch = true;
            level = node.children;
        });
    });
    const sortByName = (nodes: FilteredFolderNode[]): FilteredFolderNode[] =>
        nodes
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
            .map(n => ({ ...n, children: sortByName(n.children) }));
    return sortByName(roots);
};

// Recursively walks the nested tree to find the node matching `treeNodeId`,
// applies `updater` to it, and returns a new tree with that node replaced.
const updateNode = (nodes: TreeNode[], treeNodeId: string, updater: (n: TreeNode) => TreeNode): TreeNode[] =>
    nodes.map(n => n.id === treeNodeId ? updater(n) : { ...n, children: updateNode(n.children, treeNodeId, updater) });

// Find the parent of a target node
const findParent = (nodes: TreeNode[], targetId: string, parentId: string | null = null): string | null => {
    for (const node of nodes) {
        if (node.id === targetId) return parentId;
        const found = findParent(node.children, targetId, node.id);
        if (found !== null) return found;
    }
    return null;
};

// Find the path from root to a target node
const findPath = (nodes: TreeNode[], targetId: string): boolean => {
    for (const node of nodes) {
        if (node.id === targetId) return true;
        if (findPath(node.children, targetId)) return true;
    }
    return false;
};

// Build the breadcrumb path (array of node names) from root down to targetId
const findPathToNode = (nodes: TreeNode[], targetId: string, path: string[] = []): string[] | null => {
    for (const n of nodes) {
        const current = path.concat(n.name);
        if (n.id === targetId) return current;
        const found = findPathToNode(n.children, targetId, current);
        if (found) return found;
    }
    return null;
};

const findNodePath = (nodes: TreeNode[], targetId: string, path: TreeNode[] = []): TreeNode[] | null => {
    for (const node of nodes) {
        const current = path.concat(node);
        if (node.id === targetId) return current;
        const found = findNodePath(node.children, targetId, current);
        if (found) return found;
    }
    return null;
};

const findNodeById = (nodes: TreeNode[], targetId: string): TreeNode | null => {
    for (const n of nodes) {
        if (n.id === targetId) return n;
        const found = findNodeById(n.children, targetId);
        if (found) return found;
    }
    return null;
};

// The search API returns the document name as the last segment of fullPath; the row already shows it.
const getSearchDocumentFolderPath = (fullPath?: string | null, documentName?: string | null): string => {
    const segments = (fullPath ?? '').split('>').map(s => s.trim()).filter(Boolean);
    if (documentName && segments[segments.length - 1] === documentName.trim()) {
        segments.pop();
    }
    return segments.join('>');
};

// Accordion: collapse siblings of a target node at the same level, keep ancestor path expanded
const setAccordionAtLevel = (nodes: TreeNode[], targetId: string): TreeNode[] => {
    const parentId = findParent(nodes, targetId);
    
    return nodes.map(n => {
        // If this is the parent, collapse its other children (siblings of target)
        if (parentId === null && n.id === targetId) {
            // Target is at root level - keep it expanded
            return { ...n, isExpanded: true, children: n.children };
        } else if (parentId === null && n.id !== targetId) {
            // Other root level nodes - collapse them
            return { ...n, isExpanded: false, children: n.children };
        } else if (n.id === parentId) {
            // This is the parent of target - keep it expanded and handle its children
            return {
                ...n,
                isExpanded: true,
                children: n.children.map(child => 
                    child.id === targetId 
                        ? { ...child, isExpanded: true }
                        : { ...child, isExpanded: false }
                )
            };
        } else {
            // Other nodes - check if target is in this subtree 
            const isTargetInSubtree = findPath(n.children, targetId);
            return {
                ...n,
                // collapse branches that don't contain the target
                isExpanded: isTargetInSubtree ? n.isExpanded : false,
                // Recurse only if target is in this subtree
                children: isTargetInSubtree ? setAccordionAtLevel(n.children, targetId) : n.children
            };
        }
    });
};

// Helper: Get all visible node IDs in order (for keyboard navigation)
const getAllVisibleNodeIds = (nodes: TreeNode[]): string[] => {
    const result: string[] = [];
    const traverse = (nodeList: TreeNode[]) => nodeList.forEach(n => {
        result.push(n.id);
        if (n.isExpanded) traverse(n.children);
    });
    traverse(nodes);
    return result;
};

const getDownloadFileMeta = (rawFileName: string, fileExtension?: string | null): { fileName: string; isUnknownExt: boolean } => {
    const normalizedExt = (fileExtension || '').toLowerCase().replace('.', '');
    const isUnknownExt = normalizedExt === 'unknown' || /\.unknown$/i.test(rawFileName);
    const fileName = isUnknownExt ? rawFileName.replace(/\.unknown$/i, '').replace(/\.$/, '') : rawFileName;
    return { fileName, isUnknownExt };
};

const getUploadFileKey = (file: File): string => `${file.name}__${file.size}__${file.lastModified}`;
const MAX_UPLOAD_FILE_SIZE_LABEL = `${MAX_UPLOAD_FILE_SIZE_MB} MB`;

const getFileExtension = (fileName: string): string => {
    const segments = fileName.split('.');
    return segments.length > 1 ? segments.pop()?.toLowerCase() || '' : '';
};

const isUploadFileSizeAllowed = (file: File): boolean => file.size <= MAX_UPLOAD_FILE_SIZE_BYTES;

const AdminDocumentViewer: React.FC = () => {
    const sanitizeAllowedInput = (value: string): string => {
        if (!SEARCH_ALLOWED_PATTERN.test(value)) {
            toast.warn(SEARCH_INVALID_CHARACTERS_MESSAGE, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }
        return value.replace(SEARCH_DISALLOWED_CHARACTERS_PATTERN, '');
    };

    const adminDocumentServiceObj = new AdminDocumentService();
    const userDetails = useSelector((state: any) => state.userContextSlice);
    const ability = useContext(AbilityContext);
    const canCreateAdminDocument = ability.can('create', constant.ScreenName.CS2_Main_Menu, constant.SidebarPrivileges.Documate_AdminDocument);
    const lastPagedNodeIdRef = useRef<string | null>(null);
    const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);
    const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
    const [panelDocuments, setPanelDocuments] = useState<DocFileReadDto[]>([]);
    const [gridColumns, setGridColumns] = useState<any[]>([]);
    const [selectedDocument, setSelectedDocument] = useState<SelectedDocument | null>(null);
    const [selectedDocumentName, setSelectedDocumentName] = useState('');
    const [isInitializing, setIsInitializing] = useState(true);
    const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
    const treeItemRefs = useRef<{ [nodeId: string]: HTMLLIElement }>({});
    const hasSetInitialTreeFocus = useRef(false);
    const documentGridRef = useRef<any>(null);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [pendingDownload, setPendingDownload] = useState<{ blob: Blob; fileName: string; contentType?: string; isUnknownExt?: boolean } | null>(null);
    const [downloadModalTitle, setDownloadModalTitle] = useState<string>(UNSUPPORTED_FILE_FORMAT);
    const [downloadModalMessage, setDownloadModalMessage] = useState('');
    const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const selectedDocIdRef = useRef<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<AdminDocumentSearchResponseDto | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [activeSearchText, setActiveSearchText] = useState('');
    const [searchFilter, setSearchFilter] = useState<'all' | 'documents' | 'folders'>('all');
    const [folderFilterText, setFolderFilterText] = useState('');
    const [filteredFolderTree, setFilteredFolderTree] = useState<FilteredFolderNode[] | null>(null);
    const [isFolderFiltering, setIsFolderFiltering] = useState(false);
    const folderFilterRequestRef = useRef(0);
    const [documentFilterText, setDocumentFilterText] = useState('');
    const [appliedDocumentFilter, setAppliedDocumentFilter] = useState('');
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [documentNotes, setDocumentNotes] = useState<NoteDto[]>([]);
    const [newDocumentNote, setNewDocumentNote] = useState('');
    const [notesLoadError, setNotesLoadError] = useState(false);
    const [isSavingNote, setIsSavingNote] = useState(false);
    const savingNoteRef = useRef(false);
    const notesEntityIdRef = useRef<string | null>(null);
    const notesRequestRef = useRef(0);
    const handleCloseNotes = () => {
        if (savingNoteRef.current) return;
        notesRequestRef.current += 1;
        setShowNotesModal(false);
        notesEntityIdRef.current = null;
    };

    const handleOpenNotes = async () => {
        if (!selectedDocumentId || savingNoteRef.current) return;
        notesEntityIdRef.current = selectedDocumentId;
        const requestId = ++notesRequestRef.current;
        setDocumentNotes([]);
        setNewDocumentNote('');
        setNotesLoadError(false);
        setShowNotesModal(true);
        try {
            const notes = await adminDocumentServiceObj.getNotesByEntityId(selectedDocumentId);
            if (requestId !== notesRequestRef.current) return;
            setDocumentNotes([...notes].sort((left, right) => {
                const dateDifference = Date.parse(right.enteredDate) - Date.parse(left.enteredDate);
                return Number.isNaN(dateDifference) ? 0 : dateDifference;
            }));
        } catch (error) {
            if (requestId !== notesRequestRef.current) return;
            setNotesLoadError(true);
            toast.error('Failed to load notes.');
        }
    };

    const handleSubmitNote = async () => {
        const entityId = notesEntityIdRef.current;
        if (!entityId || notesLoadError || savingNoteRef.current) return;
        const notes = newDocumentNote.trim();
        if (!notes) {
            toast.warn('Please enter a note before submitting.');
            return;
        }
        savingNoteRef.current = true;
        setIsSavingNote(true);
        try {
            const note = await adminDocumentServiceObj.addNote(entityId, notes);
            setDocumentNotes(previousNotes => [note, ...previousNotes]);
            setNewDocumentNote('');
            toast.success('Note added successfully!');
        } catch (error) {
            toast.error(ADD_NOTE_ERROR);
        } finally {
            savingNoteRef.current = false;
            setIsSavingNote(false);
        }
    };

    // ── Delete state ──
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [docPendingDelete, setDocPendingDelete] = useState<DocFileReadDto | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Tracks which document row's action menu is open, so only one can be open at a time.
    const openRowActionsIdRef = useRef<string | null>(null);
    const [, setRowActionsRenderTick] = useState(0);
    const setOpenRowActionsId = (id: string | null) => {
        openRowActionsIdRef.current = id;
        setRowActionsRenderTick(t => t + 1);
    };

    // ── Upload modal state ──
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadDocumentTypes, setUploadDocumentTypes] = useState<{ value: string; label: string }[]>([]);
    const [uploadDocumentTypeId, setUploadDocumentTypeId] = useState('');
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [uploadDragOver, setUploadDragOver] = useState(false);
    const [uploadValidation, setUploadValidation] = useState({ documentType: false, files: false });
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({
        current: 0,
        total: 0,
        successCount: 0,
        failureCount: 0,
        successFileNames: [] as string[],
        failureFileNames: [] as string[],
        isProcessing: false,
        isComplete: false
    });
    const [selectedNodePath, setSelectedNodePath] = useState<string>('');
    const uploadFileInputRef = useRef<HTMLInputElement>(null);

    // Folder creation state
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [createFolderParentNode, setCreateFolderParentNode] = useState<TreeNode | null>(null);
    const [createFolderName, setCreateFolderName] = useState('');
    const [createFolderValidation, setCreateFolderValidation] = useState({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
    const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
    const [showNodeMenu, setShowNodeMenu] = useState<string | null>(null);
    const nodeMenuRef = useRef<HTMLDivElement>(null);

    // Folder rename state
    const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
    const [renameFolderNode, setRenameFolderNode] = useState<TreeNode | null>(null);
    const [renameFolderName, setRenameFolderName] = useState('');
    const [renameFolderValidation, setRenameFolderValidation] = useState({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
    
    const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
    const [folderPendingDelete, setFolderPendingDelete] = useState<TreeNode | null>(null);
    const [folderDocumentCount, setFolderDocumentCount] = useState(0);
    const [deleteFolderConfirmed, setDeleteFolderConfirmed] = useState(false);
    const [isDeletingFolder, setIsDeletingFolder] = useState(false);

    const resetUploadModal = () => {
        setUploadDocumentTypeId('');
        setUploadFiles([]);
        setUploadDragOver(false);
        setUploadValidation({ documentType: false, files: false });
    };

    const handleUploadButtonClick = async () => {
        resetUploadModal();
        setShowUploadModal(true);
        try {
            const types = await adminDocumentServiceObj.getListOfDocumentType({ AppId: String(selectedNode?.appId) });
            setUploadDocumentTypes(types);
        } catch (err) {
            Logger.logError(`Error fetching document types: ${err?.toString()}`);
            toast.error(constant.ERROR_MESSAGE_LOAD, { position: toast.POSITION.TOP_RIGHT, autoClose: constant.AUTO_CLOSE });
        }
    };

    const handleUploadFilesAdd = (newFiles: FileList | File[] | null) => {
        if (!newFiles) return;
        const incomingFiles = Array.isArray(newFiles) ? newFiles : Array.from(newFiles);
        if (incomingFiles.length === 0) return;

        const filesWithAllowedType = incomingFiles.filter(f => ALLOWED_UPLOAD_EXTENSIONS.has(getFileExtension(f.name)));
        const invalidFiles = incomingFiles.filter(f => !ALLOWED_UPLOAD_EXTENSIONS.has(getFileExtension(f.name)));
        const oversizedFiles = filesWithAllowedType.filter(f => !isUploadFileSizeAllowed(f));
        const validFiles = filesWithAllowedType.filter(isUploadFileSizeAllowed);

        if (invalidFiles.length > 0) {
            const invalidNames = invalidFiles.map(f => f.name).join(', ');
            toast.error(`Unsupported file type: ${invalidNames}`, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }

        if (oversizedFiles.length > 0) {
            const oversizedNames = oversizedFiles.map(f => f.name).join(', ');
            toast.error(`File size exceeds ${MAX_UPLOAD_FILE_SIZE_LABEL}: ${oversizedNames}`, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }

        if (validFiles.length === 0) return;

        setUploadFiles(prev => {
            const existing = new Set(prev.map(getUploadFileKey));
            const added = validFiles.filter(f => !existing.has(getUploadFileKey(f)));
            return prev.concat(added);
        });
        setUploadValidation(v => ({ ...v, files: false }));
    };

    const handleUploadFileRemove = (index: number) => {
        setUploadFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUploadSubmit = async () => {
        if (!selectedNode) return;

        const disallowedFiles = uploadFiles.filter(f => !ALLOWED_UPLOAD_EXTENSIONS.has(getFileExtension(f.name)));
        if (disallowedFiles.length > 0) {
            const invalidNames = disallowedFiles.map(f => f.name).join(', ');
            toast.error(`Unsupported file type: ${invalidNames}`, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            return;
        }

        const hasDocTypeError = !uploadDocumentTypeId;
        const hasFilesError = uploadFiles.length === 0;
        if (hasDocTypeError || hasFilesError) {
            setUploadValidation({ documentType: hasDocTypeError, files: hasFilesError });
            return;
        }

        setIsUploading(true);
        setShowUploadModal(false);
        const userId = userDetails?.userContext?.id ?? '';
        const indexKey1 = selectedNode?.id ?? '';
        const queuePath = selectedNode ? (findPathToNode(treeNodes, selectedNode.id)?.join('/') ?? '') : '';
        setUploadProgress({
            current: 0,
            total: uploadFiles.length,
            successCount: 0,
            failureCount: 0,
            successFileNames: [],
            failureFileNames: [],
            isProcessing: true,
            isComplete: false
        });

        let successCount = 0;
        let failureCount = 0;
        const successFileNames: string[] = [];
        const failureFileNames: string[] = [];

        for (let i = 0; i < uploadFiles.length; i++) {
            const file = uploadFiles[i];
            try {
                const fileExtension = getFileExtension(file.name);
                const payload = {
                    appId: selectedNode.appId,
                    fileName: file.name,
                    contentType: fileExtension === 'msg'
                        ? OUTLOOK_MSG_MIME_TYPE
                        : (file.type || 'application/octet-stream'),
                    documentTypeId: uploadDocumentTypeId,
                    indexKey1,
                    indexKey2: '',
                    attributes: '',
                    userId,
                    queuePath
                };
                const response = await UploadCustomerDocument(file, payload);
                if (response?.docFileId) {
                    successCount++;
                    successFileNames.push(file.name);
                } else {
                    failureCount++;
                    failureFileNames.push(file.name);
                }
            } catch (err) {
                Logger.logError(`Error uploading file ${file.name}: ${err?.toString()}`);
                failureCount++;
                failureFileNames.push(file.name);
            }

            setUploadProgress({
                current: i + 1,
                total: uploadFiles.length,
                successCount,
                failureCount,
                successFileNames: [...successFileNames],
                failureFileNames: [...failureFileNames],
                isProcessing: true,
                isComplete: false
            });
        }

        setIsUploading(false);
        setUploadProgress({
            current: uploadFiles.length,
            total: uploadFiles.length,
            successCount,
            failureCount,
            successFileNames,
            failureFileNames,
            isProcessing: false,
            isComplete: true
        });

        if (successCount > 0) {
            // Refresh the document list for the current folder
            if (selectedNode) {
                const contents = await adminDocumentServiceObj.fetchDocFolder(
                    selectedNode.appId,
                    selectedNode.type === DOC_NODE_TYPE_CATEGORY ? selectedNode.id : undefined,
                    selectedNode.type === DOC_NODE_TYPE_FOLDER ? selectedNode.id : undefined
                );
                const refreshedDocuments = contents?.document ?? [];
                setPanelDocuments(refreshedDocuments);

                // Keep the node caches in step. handleTreeNodeClick serves a loaded node's documents
                // from cache, so without this the uploaded files disappear when the folder is revisited.
                setSelectedNode(prev => prev ? { ...prev, documents: refreshedDocuments } : prev);
                setTreeNodes(prev => updateNode(prev, selectedNode.id, n => ({
                    ...n,
                    documents: refreshedDocuments
                })));
            }
        }
    };

    const handleUploadProgressComplete = () => {
        setUploadProgress({
            current: 0,
            total: 0,
            successCount: 0,
            failureCount: 0,
            successFileNames: [],
            failureFileNames: [],
            isProcessing: false,
            isComplete: false
        });
        resetUploadModal();
    };

    //Folder creation handlers
    const resetCreateFolderModal = () => {
        setCreateFolderName('');
        setCreateFolderValidation({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
    };

    const handleNodeMenuToggle = (nodeId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setShowNodeMenu(prev => prev === nodeId ? null : nodeId);
    };

    const handleAddSubFolderClick = (node: TreeNode, e: React.MouseEvent) => {
        e.stopPropagation();
        setShowNodeMenu(null);
        setCreateFolderParentNode(node);
        setShowCreateFolderModal(true);
        resetCreateFolderModal();
    };

    const handleDeleteFolderClick = async (node: TreeNode, e: React.MouseEvent) => {
        e.stopPropagation();
        setShowNodeMenu(null);
        try {
            const count = await adminDocumentServiceObj.fetchAdminFolderDocumentCount(node.appId, node.id);
            setFolderPendingDelete(node);
            setFolderDocumentCount(count);
            setDeleteFolderConfirmed(false);
            setShowDeleteFolderModal(true);
        } catch (err) {
            Logger.logError(`Error fetching document count for folder ${node.id}: ${err?.toString()}`);
            toast.error(DELETE_FOLDER_COUNT_ERROR, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }
    };

    const handleDeleteFolderCancel = () => {
        setShowDeleteFolderModal(false);
        setFolderPendingDelete(null);
        setFolderDocumentCount(0);
        setDeleteFolderConfirmed(false);
    };

    const handleDeleteFolderConfirm = async () => {
        const deletedFolder = folderPendingDelete;
        if (!deletedFolder) return;

        setIsDeletingFolder(true);
        try {
            const result = await adminDocumentServiceObj.deleteFolder(deletedFolder.appId, deletedFolder.id);
            const isFolderDeleted = result.deletedFolders.some(id => id?.toLowerCase() === deletedFolder.id.toLowerCase());
            if (!isFolderDeleted) {
                toast.error(DELETE_FOLDER_ERROR, { position: toast.POSITION.TOP_RIGHT, autoClose: constant.AUTO_CLOSE });
                return;
            }
            toast.success(DELETE_FOLDER_SUCCESS, { position: toast.POSITION.TOP_RIGHT, autoClose: constant.AUTO_CLOSE });
        } catch (err) {
            Logger.logError(`Error deleting folder ${deletedFolder.id}: ${err?.toString()}`);
            toast.error(DELETE_FOLDER_ERROR, { position: toast.POSITION.TOP_RIGHT, autoClose: constant.AUTO_CLOSE });
            return;
        } finally {
            setIsDeletingFolder(false);
        }

        const parentId = findParent(treeNodes, deletedFolder.id);
        const parentNode = parentId ? findNodeById(treeNodes, parentId) : null;
        const isSelectedDeleted = !!selectedNode &&
            (selectedNode.id === deletedFolder.id || findPath(deletedFolder.children, selectedNode.id));

        setTreeNodes(prev => parentId
            ? updateNode(prev, parentId, p => ({ ...p, children: p.children.filter(c => c.id !== deletedFolder.id) }))
            : prev);
        setSelectedNode(prev => prev && prev.id === parentId
            ? { ...prev, children: prev.children.filter(c => c.id !== deletedFolder.id) }
            : prev);
        handleDeleteFolderCancel();

        if (isSelectedDeleted && parentNode) {
            // isLoaded: false forces the parent's contents to be re-fetched.
            await handleTreeNodeClick({
                ...parentNode,
                children: parentNode.children.filter(c => c.id !== deletedFolder.id),
                isLoaded: false
            });
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (nodeMenuRef.current && !nodeMenuRef.current.contains(event.target as Node)) {
                setShowNodeMenu(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getHasNodeActions = (node: TreeNode): boolean => {
        // All 5 top-level categories allow folder creation directly under them
        if (node.type === DOC_NODE_TYPE_CATEGORY) return true;
        // All folders under categories also allow sub-folder creation
        if (node.type === DOC_NODE_TYPE_FOLDER) return true;
        return false;
    };

    // Rename applies to any folder node (level 1 and deeper); only the 5 top-level
    // category nodes (categoryId, no folder id) are excluded.
    const getCanRenameNode = (node: TreeNode): boolean => node.type === DOC_NODE_TYPE_FOLDER;

    const handleCreateFolderSubmit = async () => {
        const trimmedName = createFolderName.trim();
        if (!trimmedName) {
            setCreateFolderValidation({ name: true, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
            return;
        }

        if (trimmedName.length < FOLDER_NAME_MIN_LENGTH) {
            setCreateFolderValidation({ name: false, exists: false, tooShort: true, tooLong: false, forbiddenChars: false, trailingChars: false });
            return;
        }

        if (trimmedName.length > FOLDER_NAME_MAX_LENGTH) {
            setCreateFolderValidation({ name: false, exists: false, tooShort: false, tooLong: true, forbiddenChars: false, trailingChars: false });
            return;
        }

        if (FOLDER_NAME_FORBIDDEN_CHARACTERS_PATTERN.test(trimmedName) || !SEARCH_ALLOWED_PATTERN.test(trimmedName)) {
            setCreateFolderValidation({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: true, trailingChars: false });
            return;
        }

        if (FOLDER_NAME_TRAILING_CHARACTERS_PATTERN.test(trimmedName)) {
            setCreateFolderValidation({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: true });
            return;
        }

        if (!createFolderParentNode) return;

        // Check uniqueness (case-insensitive) among siblings
        if (createFolderParentNode.children.some(child => 
            child.name.toLowerCase() === trimmedName.toLowerCase()
        )) {
            setCreateFolderValidation({ name: false, exists: true, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
            return;
        }

        try {
            const result = await adminDocumentServiceObj.createFolder(
                trimmedName,
                createFolderParentNode.type === DOC_NODE_TYPE_CATEGORY ? createFolderParentNode.id : undefined,
                createFolderParentNode.type === DOC_NODE_TYPE_FOLDER ? createFolderParentNode.id : undefined
            );

            const newFolder: TreeNode = {
                id: result.id,
                name: result.name,
                appId: createFolderParentNode.appId,
                type: DOC_NODE_TYPE_FOLDER,
                children: [],
                documents: [],
                isExpanded: false,
                isLoaded: false
            };

            // Insert alphabetically among siblings
            setTreeNodes(prev => {
                const updated = updateNode(prev, createFolderParentNode.id, parent => {
                    const newChildren = [...parent.children, newFolder].sort((a, b) =>
                        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
                    );
                    return {
                        ...parent,
                        children: newChildren,
                        isExpanded: true,
                        isLoaded: true
                    };
                });
                return updated;
            });

            // Select the new folder
            setSelectedNode(newFolder);
            setPanelDocuments([]);
            const parentPath = findPathToNode(treeNodes, createFolderParentNode.id);
            const fullPath = parentPath ? [...parentPath, result.name] : [result.name];
            setSelectedNodePath(fullPath.join(' / '));

            toast.success(`${FOLDER_CREATED_SUCCESS} — ${result.name} added under ${createFolderParentNode.name}`, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });

            setShowCreateFolderModal(false);
            resetCreateFolderModal();
        } catch (err) {
            Logger.logError(`Error creating folder: ${err?.toString()}`);
            toast.error(FOLDER_CREATION_ERROR, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }
    };

    // Folder rename handlers
    const resetRenameFolderModal = () => {
        setRenameFolderNode(null);
        setRenameFolderName('');
        setRenameFolderValidation({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
    };

    const handleRenameFolderClick = (node: TreeNode, e: React.MouseEvent) => {
        e.stopPropagation();
        setShowNodeMenu(null);
        setRenameFolderNode(node);
        setRenameFolderName(node.name);
        setRenameFolderValidation({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
        setShowRenameFolderModal(true);
    };

    const handleRenameFolderSubmit = async () => {
        if (!renameFolderNode) return;
        const trimmedName = renameFolderName.trim();

        if (!trimmedName) {
            setRenameFolderValidation({ name: true, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
            return;
        }

        if (trimmedName.length < FOLDER_NAME_MIN_LENGTH) {
            setRenameFolderValidation({ name: false, exists: false, tooShort: true, tooLong: false, forbiddenChars: false, trailingChars: false });
            return;
        }

        if (trimmedName.length > FOLDER_NAME_MAX_LENGTH) {
            setRenameFolderValidation({ name: false, exists: false, tooShort: false, tooLong: true, forbiddenChars: false, trailingChars: false });
            return;
        }

        if (FOLDER_NAME_FORBIDDEN_CHARACTERS_PATTERN.test(trimmedName) || !SEARCH_ALLOWED_PATTERN.test(trimmedName)) {
            setRenameFolderValidation({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: true, trailingChars: false });
            return;
        }

        if (FOLDER_NAME_TRAILING_CHARACTERS_PATTERN.test(trimmedName)) {
            setRenameFolderValidation({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: true });
            return;
        }

        // Check uniqueness (case-insensitive) among siblings, excluding itself
        const parentId = findParent(treeNodes, renameFolderNode.id);
        const siblings = parentId ? (findNodeById(treeNodes, parentId)?.children ?? []) : treeNodes;
        if (siblings.some(sibling =>
            sibling.id !== renameFolderNode.id && sibling.name.toLowerCase() === trimmedName.toLowerCase()
        )) {
            setRenameFolderValidation({ name: false, exists: true, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
            return;
        }

        const previousName = renameFolderNode.name;
        try {
            const result = await adminDocumentServiceObj.renameFolder(renameFolderNode.id, trimmedName);

            let updatedTree: TreeNode[] = treeNodes;
            setTreeNodes(prev => {
                updatedTree = updateNode(prev, renameFolderNode.id, n => ({ ...n, name: result.name }));
                return updatedTree;
            });

            // Reflect the rename in the currently selected node, breadcrumb and any open file list view.
            setSelectedNode(prev => (prev && prev.id === renameFolderNode.id ? { ...prev, name: result.name } : prev));
            if (selectedNode) {
                const newPath = findPathToNode(updatedTree, selectedNode.id);
                setSelectedNodePath(newPath ? newPath.join(' / ') : selectedNode.name);
            }

            toast.success(`${FOLDER_RENAMED_SUCCESS} — ${previousName} renamed to ${result.name}`, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });

            setShowRenameFolderModal(false);
            resetRenameFolderModal();
        } catch (err: any) {
            Logger.logError(`Error renaming folder ${renameFolderNode.id}: ${err?.toString()}`);
            const status = err?.response?.status;
            // Modal stays open and the entered name is preserved on failure — do not reset.
            toast.error(status === 404 ? FOLDER_NOT_FOUND_ERROR : FOLDER_RENAME_ERROR, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }
    };

    const fetchCategories = async () => {
            try {
                const data = await adminDocumentServiceObj.fetchDocCategory();
                const nodes: TreeNode[] = data.map(category => ({
                    id: category.id,
                    name: category.name,
                    appId: category.appId,
                    type: DOC_NODE_TYPE_CATEGORY,
                    children: [],
                    documents: [],
                    isExpanded: false,
                    isLoaded: false
                }));
                setTreeNodes(nodes);
                setFocusedNodeId(currentId => currentId ?? nodes[0]?.id ?? null);
                setIsInitializing(false);
            } catch (err) {
                Logger.logError(`Error fetching doc categories: ${err?.toString()}`);
                toast.error(constant.ERROR_MESSAGE_LOAD, {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: constant.AUTO_CLOSE
                });
                setIsInitializing(false);
            }
        };

    const handleAdminDocumentsBreadcrumbClick = async () => {
        setIsInitializing(true);
        setTreeNodes([]);
        setSelectedNode(null);
        setPanelDocuments([]);
        setSelectedDocument(null);
        setSelectedNodePath('');
        setFocusedNodeId(null);
        setSearchTerm('');
        setSearchResults(null);
        setActiveSearchText('');
        setSearchFilter('all');
        clearFolderFilter();
        setOpenRowActionsId(null);
        selectedDocIdRef.current = null;
        setSelectedDocumentId(null);
        setSelectedDocumentName('');
        await fetchCategories();
    };

    useEffect(() => {    
        fetchCategories();
    }, []);

    // Keep the selected node visible in the tree panel once its row is rendered.
    useEffect(() => {
        if (!selectedNode) return;
        const timer = setTimeout(() => {
            treeItemRefs.current[selectedNode.id]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 0);
        return () => clearTimeout(timer);
    }, [selectedNode?.id, treeNodes]);

    useEffect(() => {
        setDocumentFilterText('');
        setAppliedDocumentFilter('');
    }, [selectedNode?.id]);

    // retainGridState keeps the previous pageIndex, which would leave the new folder on a page it does not have.
    useEffect(() => {
        if (!selectedNode) {
            lastPagedNodeIdRef.current = null;
            return;
        }
        if (lastPagedNodeIdRef.current === selectedNode.id) return;
        lastPagedNodeIdRef.current = selectedNode.id;
        documentGridRef.current?.gotoPage(0);
    }, [selectedNode?.id, panelDocuments]);

    const getIsUploadEnabled = (node: TreeNode): boolean => {
        // Upload is allowed on any folder level, but never on top category nodes.
        return canCreateAdminDocument && node.type === DOC_NODE_TYPE_FOLDER;
    };

    const breadcrumbItems = [
        ...ADMIN_DOCUMENT_BREADCRUMBS.map(name => ({ name, node: null as TreeNode | null })),
        ...((selectedNode ? findNodePath(treeNodes, selectedNode.id) : null) ?? [])
            .map(node => ({ name: node.name, node }))
    ];

    const documentCount = searchResults?.document?.length ?? 0;
    const folderCount = searchResults?.folder?.length ?? 0;
    const normalizedDocumentFilter = appliedDocumentFilter.trim().toLowerCase();
    const visiblePanelDocuments = normalizedDocumentFilter
        ? panelDocuments.filter(document => (document.name ?? '').toLowerCase().includes(normalizedDocumentFilter))
        : panelDocuments;

    const closeSearchResults = () => {
        setSearchResults(null);
        setActiveSearchText('');
        setSearchFilter('all');
    };

    const clearSearch = () => {
        setSearchTerm('');
        closeSearchResults();
    };

    const handleSearch = async () => {
        const trimmedText = searchTerm.trim();
        if (!SEARCH_ALLOWED_PATTERN.test(trimmedText)) {
            setSearchResults(null);
            toast.warn(SEARCH_INVALID_CHARACTERS_MESSAGE, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            return;
        }

        if (trimmedText.length < SEARCH_MIN_LENGTH) {
            setSearchResults(null);
            toast.warn(SEARCH_MIN_LENGTH_MESSAGE, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            return;
        }

        setIsSearching(true);
        setActiveSearchText(trimmedText);
        setSearchFilter('all');
        try {
            const results = await adminDocumentServiceObj.searchAdminDocuments(trimmedText, false);
            setSearchResults(results);
        } catch (error) {
            Logger.logError(`Admin document search failed: ${error?.toString()}`);
            toast.error(SEARCH_ERROR);
            setSearchResults(null);
        } finally {
            setIsSearching(false);
        }
    };

    // Walks the id path returned by the search API, loading each level, so the node opens like a tree click.
    const revealNodeFromSearch = async (nodeIds: string[]): Promise<TreeNode | null> => {
        if (nodeIds.length === 0) return null;

        let workingTree = treeNodes;
        let targetNode: TreeNode | null = null;

        for (const nodeId of nodeIds) {
            const node = findNodeById(workingTree, nodeId);
            if (!node) break;

            let loadedNode = node;
            if (!node.isLoaded) {
                const contents = await adminDocumentServiceObj.fetchDocFolder(
                    node.appId,
                    node.type === DOC_NODE_TYPE_CATEGORY ? node.id : undefined,
                    node.type === DOC_NODE_TYPE_FOLDER ? node.id : undefined
                );
                loadedNode = {
                    ...node,
                    children: (contents?.folder ?? []).map(f => ({
                        id: f.id,
                        name: f.name,
                        appId: node.appId,
                        type: DOC_NODE_TYPE_FOLDER,
                        children: [],
                        documents: [],
                        isExpanded: false,
                        isLoaded: false
                    })),
                    documents: contents?.document ?? [],
                    isLoaded: true
                };
            }

            const isTarget = nodeId === nodeIds[nodeIds.length - 1];
            const expandedNode: TreeNode = {
                ...loadedNode,
                isExpanded: isTarget ? loadedNode.children.length > 0 : true
            };
            workingTree = updateNode(workingTree, nodeId, () => expandedNode);
            targetNode = expandedNode;
        }

        if (!targetNode) return null;

        const accordionTree = setAccordionAtLevel(workingTree, targetNode.id);
        setTreeNodes(accordionTree);
        setSelectedNode(targetNode);
        setPanelDocuments(targetNode.documents);
        setSelectedDocument(null);
        setOpenRowActionsId(null);
        selectedDocIdRef.current = null;
        setSelectedDocumentId(null);
        setSelectedDocumentName('');
        setSelectedNodePath(findPathToNode(accordionTree, targetNode.id)?.join(' / ') ?? targetNode.name);
        setFocusedNodeId(targetNode.id);
        closeSearchResults();
        return targetNode;
    };

    const handleSearchFolderClick = async (folder: DocFolderSearchResultDto) => {
        const segmentIds = (folder.pathSegments ?? []).map(segment => segment.id).filter(Boolean);
        const nodeIds = segmentIds[segmentIds.length - 1] === folder.id ? segmentIds : [...segmentIds, folder.id];

        try {
            await revealNodeFromSearch(nodeIds);
        } catch (err) {
            Logger.logError(`Error opening folder from search results: ${err?.toString()}`);
            toast.error(constant.ERROR_MESSAGE_LOAD, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }
    };

    const clearFolderFilter = () => {
        folderFilterRequestRef.current++;
        setFolderFilterText('');
        setFilteredFolderTree(null);
        setIsFolderFiltering(false);
    };

    const handleFolderFilter = async () => {
        const trimmedText = folderFilterText.trim();
        if (!SEARCH_ALLOWED_PATTERN.test(trimmedText)) {
            toast.warn(SEARCH_INVALID_CHARACTERS_MESSAGE, { position: toast.POSITION.TOP_RIGHT, autoClose: constant.AUTO_CLOSE });
            return;
        }
        if (trimmedText.length < SEARCH_MIN_LENGTH) {
            toast.warn(SEARCH_MIN_LENGTH_MESSAGE, { position: toast.POSITION.TOP_RIGHT, autoClose: constant.AUTO_CLOSE });
            return;
        }

        const requestId = ++folderFilterRequestRef.current;
        setIsFolderFiltering(true);
        try {
            const results = await adminDocumentServiceObj.searchAdminDocuments(trimmedText, true);
            // Ignore responses that arrive after the filter was cleared or re-run.
            if (requestId !== folderFilterRequestRef.current) return;
            setFilteredFolderTree(buildFilteredFolderTree(results?.folder ?? [], treeNodes));
        } catch (error) {
            if (requestId !== folderFilterRequestRef.current) return;
            Logger.logError(`Admin folder filter failed: ${error?.toString()}`);
            toast.error(SEARCH_ERROR);
            setFilteredFolderTree(null);
        } finally {
            if (requestId === folderFilterRequestRef.current) setIsFolderFiltering(false);
        }
    };

    const handleDocumentFilter = () => {
        const trimmedText = documentFilterText.trim();
        if (!trimmedText) {
            setAppliedDocumentFilter('');
            documentGridRef.current?.gotoPage(0);
            return;
        }
        if (panelDocuments.length === 0) {
            toast.warn(DOCUMENT_FILTER_NO_DOCUMENTS, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            return;
        }
        if (!SEARCH_ALLOWED_PATTERN.test(trimmedText)) {
            toast.warn(SEARCH_INVALID_CHARACTERS_MESSAGE, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            return;
        }
        if (trimmedText.length < SEARCH_MIN_LENGTH) {
            toast.warn(SEARCH_MIN_LENGTH_MESSAGE, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            return;
        }

        setAppliedDocumentFilter(trimmedText);
        documentGridRef.current?.gotoPage(0);
    };

    const handleFilteredFolderClick = async (node: FilteredFolderNode) => {
        try {
            await revealNodeFromSearch(node.nodeIds);
            clearFolderFilter();
        } catch (err) {
            Logger.logError(`Error opening folder from folder filter: ${err?.toString()}`);
            toast.error(constant.ERROR_MESSAGE_LOAD, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }
    };

    const handleSearchDocumentClick = async (searchDoc: DocFileSearchResultDto) => {
        const segmentIds = (searchDoc.pathSegments ?? [])
            .map(segment => segment.id)
            .filter(id => Boolean(id) && id !== searchDoc.docFileId && id !== searchDoc.documentId);
        const folderId = searchDoc.folderId ?? '';
        const nodeIds = folderId && segmentIds[segmentIds.length - 1] !== folderId
            ? [...segmentIds, folderId]
            : segmentIds;

        try {
            const folderNode = await revealNodeFromSearch(nodeIds);
            const documentIndex = folderNode?.documents.findIndex(d => d.id === searchDoc.docFileId) ?? -1;
            const document = documentIndex >= 0 ? folderNode?.documents[documentIndex] : undefined;
            if (!document) {
                toast.error(constant.ERROR_MESSAGE_LOAD, {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: constant.AUTO_CLOSE
                });
                return;
            }

            // The grid paginates the folder's documents, so move to the page holding the selected one.
            const gridPageSize = Number(userDetails?.userContext?.pageSize) || DOCUMENT_GRID_DEFAULT_PAGE_SIZE;
            setTimeout(() => documentGridRef.current?.gotoPage(Math.floor(documentIndex / gridPageSize)), 0);

            await handleDocumentClick(document);
        } catch (err) {
            Logger.logError(`Error opening document from search results: ${err?.toString()}`);
            toast.error(constant.ERROR_MESSAGE_LOAD, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }
    };

    const handleTreeNodeClick = async (node: TreeNode) => {
        setSelectedNode(node);
        setSelectedDocument(null);
        setOpenRowActionsId(null);
        selectedDocIdRef.current = null;
        setSelectedDocumentId(null);
        setSelectedDocumentName('');
        const path = findPathToNode(treeNodes, node.id);
        setSelectedNodePath(path ? path.join(' / ') : node.name);

        if (node.isLoaded) {
            if (node.children.length > 0) setTreeNodes(prev => setAccordionAtLevel(prev, node.id));
            setPanelDocuments(node.documents);
            return;
        }

        try {
            const contents = await adminDocumentServiceObj.fetchDocFolder(
                node.appId,
                node.type === DOC_NODE_TYPE_CATEGORY ? node.id : undefined,
                node.type === DOC_NODE_TYPE_FOLDER ? node.id : undefined
            );
           
            const folders = contents?.folder ?? [];
            const childNodes: TreeNode[] = folders.map(f => ({
                id: f.id,
                name: f.name,
                appId: node.appId,
                type: DOC_NODE_TYPE_FOLDER,
                children: [],
                documents: [],
                isExpanded: false,
                isLoaded: false
            }));
            
            const loadedNode: TreeNode = {
                ...node,
                children: childNodes,
                documents: contents?.document ?? [],
                isLoaded: true
            };

            setTreeNodes(prev => {
                const updated = updateNode(prev, node.id, () => loadedNode);
                return childNodes.length > 0 ? setAccordionAtLevel(updated, node.id) : updated;
            });

            // Reflect the now-loaded node so getIsUploadEnabled sees correct isLoaded/children
            setSelectedNode(loadedNode);
            setPanelDocuments(contents?.document ?? []);
        } catch (err) {
            Logger.logError(`Error fetching folder contents: ${err?.toString()}`);
            toast.error(constant.ERROR_MESSAGE_LOAD, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }
    };

    // Download must always return the ORIGINAL file (ConvertToPDF=false), never the blob fetched for preview.
    const openDownloadFallback = async (doc: DocFileReadDto, title: string, message: string) => {
        setSelectedDocument(null);
        setDownloadModalTitle(title);
        setDownloadModalMessage(message);
        try {
            const result = await adminDocumentServiceObj.getDocument(doc.id, false);
            if (selectedDocIdRef.current !== doc.id) return;
            const { fileName, isUnknownExt } = getDownloadFileMeta(result.filename || doc.name || doc.id, doc.fileExtension);
            setPendingDownload({ blob: result.blob, fileName, contentType: result.contentType, isUnknownExt });
            setShowDownloadModal(true);
        } catch (err) {
            Logger.logError(`Error fetching original file for download ${doc.id}: ${err?.toString()}`);
            toast.error('Failed to load document', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }
    };

    // react-pdf failed to render an already-fetched blob (e.g. corrupt converted PDF).
    const handlePreviewRenderError = (error: Error) => {
        const doc = selectedDocument?.node;
        if (!doc) return;
        Logger.logError(`Error rendering document ${doc.id}: ${error?.toString()}`);
        openDownloadFallback(doc, PREVIEW_UNAVAILABLE_TITLE, PREVIEW_FAILED_MESSAGE);
    };

    const handleDocumentClick = async (doc: DocFileReadDto) => {
        selectedDocIdRef.current = doc.id;
        setSelectedDocumentId(doc.id || null);
        setIsPreviewLoading(false);
        const docExt = (doc.fileExtension || '').replace('.', '');
        const docName = doc.name || doc.id || '';
        const hasExt = !docExt || docExt.toLowerCase() === 'unknown' || docName.toLowerCase().endsWith(`.${docExt.toLowerCase()}`);
        setSelectedDocumentName(hasExt ? docName : `${docName}.${docExt}`);
        setSelectedDocument(null);
        if (!doc.id) {
            toast.error('Document ID is missing. Cannot load document.', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            return;
        }

        const ext = (doc.fileExtension || '').toLowerCase().replace('.', '');
        const isPreviewable = !ext || PREVIEWABLE_DOCUMENT_EXTENSIONS.has(ext);
        if (!isPreviewable) {
            await openDownloadFallback(doc, UNSUPPORTED_FILE_FORMAT, UNSUPPORTED_FILE_FORMAT_MESSAGE);
            return;
        }

        setIsPreviewLoading(true);
        try {
            const result = await adminDocumentServiceObj.getDocument(doc.id);
            if (selectedDocIdRef.current !== doc.id) return;
            if (!result.blob || result.blob.size === 0) {
                await openDownloadFallback(doc, PREVIEW_UNAVAILABLE_TITLE, PREVIEW_FAILED_MESSAGE);
                return;
            }
            if (result.blob.size > MAX_PREVIEW_SIZE_BYTES) {
                await openDownloadFallback(doc, PREVIEW_UNAVAILABLE_TITLE, PREVIEW_TOO_LARGE_MESSAGE);
                return;
            }
            const { fileName } = getDownloadFileMeta(result.filename || doc.name || doc.id, doc.fileExtension);
            setSelectedDocument({
                node: doc,
                blob: result.blob,
                fileName
            });
        } catch (err) {
            Logger.logError(`Error fetching document: ${err?.toString()}`);
            await openDownloadFallback(doc, PREVIEW_UNAVAILABLE_TITLE, PREVIEW_FAILED_MESSAGE);
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const handleDownloadPendingFile = async () => {
        if (!pendingDownload) return;
        if (!pendingDownload.blob || pendingDownload.blob.size === 0) {
            toast.error('Document content is empty. Please try again.', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            setShowDownloadModal(false);
            setPendingDownload(null);
            setDownloadModalTitle(UNSUPPORTED_FILE_FORMAT);
            setDownloadModalMessage('');
            return;
        }
        try {
            const arrayBuffer = await pendingDownload.blob.arrayBuffer();
            const mimeType = pendingDownload.isUnknownExt ? 'application/octet-stream' : (pendingDownload.contentType || 'application/octet-stream');
            const freshBlob = new Blob([arrayBuffer], {
                type: mimeType
            });
            downloadFile(freshBlob, pendingDownload.fileName);
            setShowDownloadModal(false);
            setPendingDownload(null);
            setDownloadModalTitle(UNSUPPORTED_FILE_FORMAT);
            setDownloadModalMessage('');
            toast.success('Document downloaded successfully', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        } catch (err) {
            Logger.logError(`${ERROR_DOWNLOADING_DOCUMENT} ${err?.toString()}`);
            toast.error('Failed to download document. Please try again.', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        }
    };

    // ── Delete handlers ──
    // Only state setters are used here so the grid cell closures (built once on mount) stay valid.
    const handleDeleteMenuClick = (doc: DocFileReadDto) => {
        setDocPendingDelete(doc);
        setShowDeleteModal(true);
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setDocPendingDelete(null);
    };

    // Drop the documents the API reported as deleted from the panel list, the tree node's cached
    // documents and, when one of them is on screen, from the viewer panel. Only ids the API confirmed
    // are removed — it skips ids that do not exist, belong to another app, or are already deleted.
    // Ids are compared lower-cased because the API echoes back GUIDs in its own casing.
    const removeDocumentsFromState = (deletedDocIds: string[]) => {
        const deletedIdSet = new Set(deletedDocIds.filter(Boolean).map(id => id.toLowerCase()));
        const isDeletedDoc = (doc: DocFileReadDto) => deletedIdSet.has(doc.id?.toLowerCase());

        setPanelDocuments(prev => prev.filter(d => !isDeletedDoc(d)));
        setSelectedNode(prev => prev ? { ...prev, documents: prev.documents.filter(d => !isDeletedDoc(d)) } : prev);
        if (selectedNode) {
            setTreeNodes(prev => updateNode(prev, selectedNode.id, n => ({
                ...n,
                documents: n.documents.filter(d => !isDeletedDoc(d))
            })));
        }
        if (selectedDocIdRef.current && deletedIdSet.has(selectedDocIdRef.current.toLowerCase())) {
            selectedDocIdRef.current = null;
            setSelectedDocumentId(null);
            setSelectedDocumentName('');
            setSelectedDocument(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!docPendingDelete || !selectedNode) return;
        const doc = docPendingDelete;
        setIsDeleting(true);
        try {
            const deletedDocIds = await adminDocumentServiceObj.deleteDocument(selectedNode.appId, [doc.id]);
            if (deletedDocIds.length > 0) {
                removeDocumentsFromState(deletedDocIds);
                toast.success(DELETE_DOCUMENT_SUCCESS, {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: constant.AUTO_CLOSE
                });
            } else {
                // The API accepted the request but deleted nothing, so the row stays on screen.
                toast.error(DELETE_DOCUMENT_ERROR, {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: constant.AUTO_CLOSE
                });
            }
        } catch (err) {
            Logger.logError(`Error deleting document ${doc.id}: ${err?.toString()}`);
            toast.error(DELETE_DOCUMENT_ERROR, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setDocPendingDelete(null);
        }
    };

    // Handle keyboard navigation (arrow keys, Enter, Space)
    const handleTreeItemKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, node: TreeNode) => {
        const visibleIds = getAllVisibleNodeIds(treeNodes);
        const idx = visibleIds.indexOf(node.id);
        const moveFocus = (id: string) => {
            setFocusedNodeId(id);
            treeItemRefs.current[id]?.focus();
        };

        switch (e.key) {
            case 'Tab': {
                const nextIdx = e.shiftKey ? idx - 1 : idx + 1;
                if (nextIdx >= 0 && nextIdx < visibleIds.length) {
                    e.preventDefault();
                    moveFocus(visibleIds[nextIdx]);
                }
                break;
            }

            case 'ArrowDown':
            case 'ArrowUp': {
                e.preventDefault();
                const nextIdx = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
                if (nextIdx >= 0 && nextIdx < visibleIds.length) moveFocus(visibleIds[nextIdx]);
                break;
            }
            case 'ArrowRight':
                e.preventDefault();
                if (!node.isExpanded && node.children.length > 0)
                    setTreeNodes(prev => setAccordionAtLevel(prev, node.id));
                else if (!node.isLoaded) handleTreeNodeClick(node);
                break;

            case 'ArrowLeft':
                e.preventDefault();
                if (node.isExpanded)
                    setTreeNodes(prev => updateNode(prev, node.id, n => ({ ...n, isExpanded: false })));
                break;

            case 'Enter':
            case ' ':
                e.preventDefault();
                handleTreeNodeClick(node);
                break;
        }
    };

    const initializeGridColumns = () => {
        const columns = [
            {
                Header: '',
                accessor: 'fileExtension',
                width: 50,
                Cell: ({ value }: { value: string | null }) => value ? value.toUpperCase() : '-'
            },
            {
                Header: 'Name',
                accessor: 'name',
                width: 200,
                Cell: ({ value, row }: { value: string | null; row: { original: DocFileReadDto } }) => (
                    <>
                        {row.original.id === selectedDocIdRef.current && <span className="selected-doc-row-marker" />}
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDocumentClick(row.original);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDocumentClick(row.original);
                                }
                            }}
                            className="admin-doc-grid-link"
                            title={value ?? ''}
                        >
                            {value ? (value.length > 30 ? `${value.substring(0, 30)}…` : value) : '-'}
                        </span>
                    </>
                )
            },
            {
                Header: 'Doc Type',
                accessor: 'documentTypeName',
                width: 100,
                Cell: ({ value }: { value: string | null }) => value || '-'
            },
            {
                Header: 'Uploaded',
                accessor: 'createdAt',
                width: 90,
                Cell: ({ value }: { value: string | null }) => value ? new Date(value).toLocaleDateString() : '-'
            },
            {
                Header: 'By',
                accessor: 'createdBy',
                width: 120,
                Cell: ({ value }: { value: string | null }) => value || '-'
            },
            {
                Header: '',
                id: 'rowActions',
                width: 42,
                cellClassName: 'admin-doc-row-actions-cell',
                Cell: ({ row }: { row: { original: DocFileReadDto } }) => (
                    <Dropdown
                        align="end"
                        className="admin-doc-row-actions"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        show={openRowActionsIdRef.current === row.original.id}
                        onToggle={(nextShow) => setOpenRowActionsId(nextShow ? row.original.id : null)}
                    >
                        <Dropdown.Toggle
                            as="button"
                            type="button"
                            className="admin-doc-row-actions-toggle"
                            title={DOCUMENT_ACTIONS}
                            aria-label={`${DOCUMENT_ACTIONS}${row.original.name ? `: ${row.original.name}` : ''}`}
                        >
                            <FaEllipsisV size={12} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                            renderOnMount
                            popperConfig={{ strategy: 'fixed' }}
                            className="admin-doc-row-actions-menu"
                        >
                            <Dropdown.Item disabled title={ROW_ACTION_COMING_SOON}>
                                <FaRegEdit size={12} className="me-2" />
                                {ROW_ACTION_RENAME}
                            </Dropdown.Item>
                            <Dropdown.Item disabled title={ROW_ACTION_COMING_SOON}>
                                <FaExchangeAlt size={12} className="me-2" />
                                {ROW_ACTION_REPLACE}
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                                className="admin-doc-row-actions-delete"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenRowActionsId(null);
                                    handleDeleteMenuClick(row.original);
                                }}
                            >
                                <FaRegTrashAlt size={12} className="me-2" />
                                {ROW_ACTION_DELETE}
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                )
            }
        ];
        setGridColumns(columns);
    };

    useEffect(() => {
        initializeGridColumns();
    }, []);

    const renderTreeNodes = (nodes: TreeNode[], level: number = 0): React.ReactNode => (
        <ul 
            className="document-list" 
            style={{ paddingLeft: level > 0 ? '16px' : '0' }}
            role={level === 0 ? "tree" : "group"}
            aria-label={level === 0 ? "Administrative Documents" : undefined}
        >
            {nodes.map(node => (
                <React.Fragment key={node.id}>
                    <li
                        ref={(el) => {
                            if (el) {
                                treeItemRefs.current[node.id] = el;
                                if (!hasSetInitialTreeFocus.current && level === 0 && node.id === treeNodes[0]?.id) {
                                    hasSetInitialTreeFocus.current = true;
                                    el.focus();
                                }
                            }
                        }}
                        className={`document-item ${selectedNode?.id === node.id ? 'selected' : ''}`}
                        onClick={() => handleTreeNodeClick(node)}
                        onKeyDown={(e) => handleTreeItemKeyDown(e, node)}
                        onMouseEnter={() => setHoveredNodeId(node.id)}
                        onMouseLeave={() => setHoveredNodeId(null)}
                        role="treeitem"
                        aria-expanded={node.children.length > 0 ? node.isExpanded : undefined}
                        aria-level={level + 1}
                        aria-selected={selectedNode?.id === node.id}
                        tabIndex={focusedNodeId === node.id ? 0 : -1}
                        style={{ marginBottom: '2px', position: 'relative', zIndex: showNodeMenu === node.id ? 20 : undefined }}
                        title={node.name}
                    >
                        <span
                            className="me-1"
                            style={{ width: '14px', display: 'inline-block' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (node.isLoaded && node.children.length > 0) {
                                    // Toggle expansion with accordion behavior
                                    if (node.isExpanded) {
                                        // Just collapse this node
                                        setTreeNodes(prev => updateNode(prev, node.id, n => ({ ...n, isExpanded: false })));
                                    } else {
                                        // Expand this node and collapse siblings
                                        setTreeNodes(prev => setAccordionAtLevel(prev, node.id));
                                    }
                                } else if (!node.isLoaded) {
                                    // Not loaded yet - fetch and expand
                                    handleTreeNodeClick(node);
                                }
                            }}
                        >
                            {node.isLoaded
                                ? (node.children.length > 0
                                    ? (node.isExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />)
                                    : null)
                                : <FaChevronRight size={10} style={{ opacity: 0.5 }} />
                            }
                        </span>
                        {node.isExpanded
                            ? <FaRegFolderOpen size={14} className="doc-icon me-2" />
                            : <FaRegFolder size={14} className="doc-icon me-2" />
                        }
                        <span className="doc-name">{node.name}</span>
                        {getHasNodeActions(node) && (hoveredNodeId === node.id || showNodeMenu === node.id) && (
                            <div className="tree-node-menu-container" ref={showNodeMenu === node.id ? nodeMenuRef : undefined}>
                                <span
                                    className="tree-node-menu-icon"
                                    onClick={(e) => handleNodeMenuToggle(node.id, e)}
                                    title="Actions"
                                >
                                    <FaEllipsisV size={12} />
                                </span>
                                {showNodeMenu === node.id && (
                                    <div className="tree-node-dropdown-menu">
                                        {getCanRenameNode(node) && (
                                            <div
                                                className="tree-node-menu-item"
                                                onClick={(e) => handleRenameFolderClick(node, e)}
                                            >
                                                <FaRegEdit size={12} className="me-2" />
                                                Rename
                                            </div>
                                        )}
                                        <div 
                                            className={`tree-node-menu-item${!canCreateAdminDocument ? ' disabled' : ''}`}
                                            onClick={canCreateAdminDocument ? (e) => handleAddSubFolderClick(node, e) : undefined}
                                        >
                                            <FaPlus size={12} className="me-2" />
                                            {FOLDER_ACTION_ADD}
                                        </div>
                                        {node.type === DOC_NODE_TYPE_FOLDER && (
                                            <div
                                                className="tree-node-menu-item"
                                                onClick={(e) => handleDeleteFolderClick(node, e)}
                                            >
                                                <FaRegTrashAlt size={12} className="me-2" />
                                                {ROW_ACTION_DELETE}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </li>
                    {node.isExpanded && node.children.length > 0 && renderTreeNodes(node.children, level + 1)}
                </React.Fragment>
            ))}
        </ul>
    );

    const renderFilteredFolderNodes = (nodes: FilteredFolderNode[], level: number = 0): React.ReactNode => (
        <ul
            className="document-list"
            style={{ paddingLeft: level > 0 ? '16px' : '0' }}
            role={level === 0 ? 'tree' : 'group'}
            aria-label={level === 0 ? 'Filtered folders' : undefined}
        >
            {nodes.map(node => (
                <React.Fragment key={node.nodeIds.join('/')}>
                    <li
                        className="document-item"
                        onClick={() => handleFilteredFolderClick(node)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleFilteredFolderClick(node);
                            }
                        }}
                        role="treeitem"
                        aria-expanded={node.children.length > 0 ? node.isExpanded : undefined}
                        aria-level={level + 1}
                        tabIndex={0}
                        style={{ marginBottom: '2px' }}
                        title={node.name}
                    >
                        <span
                            className="me-1"
                            style={{ width: '14px', display: 'inline-block' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (node.children.length > 0) {
                                    setFilteredFolderTree(prev => prev ? toggleFilteredNode(prev, node.nodeIds.join('/')) : prev);
                                }
                            }}
                        >
                            {node.children.length > 0 && (node.isExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />)}
                        </span>
                        {node.children.length > 0 && node.isExpanded
                            ? <FaRegFolderOpen size={14} className="doc-icon me-2" />
                            : <FaRegFolder size={14} className="doc-icon me-2" />
                        }
                        <span className={`doc-name${node.isMatch ? ' filtered-match' : ''}`}>{node.name}</span>
                    </li>
                    {node.children.length > 0 && node.isExpanded && renderFilteredFolderNodes(node.children, level + 1)}
                </React.Fragment>
            ))}
        </ul>
    );

    const renderTreeBody = (): React.ReactNode => {
        if (isFolderFiltering) {
            return <div className="empty-state"><p className="text-muted">{SEARCH_IN_PROGRESS}</p></div>;
        }
        if (filteredFolderTree) {
            return filteredFolderTree.length > 0 ? renderFilteredFolderNodes(filteredFolderTree) : (
                <div className="empty-state"><p className="text-muted">{FOLDER_FILTER_NO_RESULTS}</p></div>
            );
        }
        return treeNodes.length > 0 ? renderTreeNodes(treeNodes) : (
            <div className="empty-state">
                <p className="text-muted">No categories available.</p>
            </div>
        );
    };

    return (
        <>
            <Modal
                verticallyCentered={true}
                title={downloadModalTitle}
                show={showDownloadModal}
                toggle={() => { setShowDownloadModal(false); setPendingDownload(null); setDownloadModalTitle(UNSUPPORTED_FILE_FORMAT); setDownloadModalMessage(''); }}
                buttonsAlignment="right"
                body={
                    <div>
                        <p><strong>{pendingDownload?.fileName}</strong></p>
                        <p>{downloadModalMessage}</p>
                    </div>
                }
                middleButton={{
                    size: 'small',
                    outlined: true,
                    color: 'primary',
                    onClick() { setShowDownloadModal(false); setPendingDownload(null); setDownloadModalTitle(UNSUPPORTED_FILE_FORMAT); setDownloadModalMessage(''); },
                    children: <span>{CANCEL}</span>
                }}
                successButton={{
                    size: 'small',
                    color: 'primary',
                    onClick: handleDownloadPendingFile,
                    children: <span>{DOWNLOAD}</span>
                }}
            />

            {/* ── Delete confirmation ── */}
            <Modal
                verticallyCentered={true}
                title={DELETE_DOCUMENT_TITLE}
                show={showDeleteModal}
                toggle={handleDeleteCancel}
                buttonsAlignment="right"
                body={
                    <div>
                        <p className="mb-1">
                            Are you sure you want to delete <strong>{docPendingDelete?.name ?? ''}</strong>?
                        </p>
                        <p className="text-muted mb-0">{DELETE_DOCUMENT_WARNING}</p>
                    </div>
                }
                middleButton={{
                    size: 'small',
                    icon: 'cancel',
                    iconClassName: 'admin-doc-confirm-btn-icon',
                    color: 'link',
                    additionalClassName: 'admin-doc-confirm-btn admin-doc-confirm-btn-no',
                    disabled: isDeleting,
                    onClick: handleDeleteCancel,
                    children: <span>{CONFIRM_NO.toUpperCase()}</span>
                }}
                successButton={{
                    size: 'small',
                    icon: 'check',
                    iconClassName: 'admin-doc-confirm-btn-icon',
                    color: 'link',
                    additionalClassName: 'admin-doc-confirm-btn admin-doc-confirm-btn-yes',
                    disabled: isDeleting,
                    onClick: handleDeleteConfirm,
                    children: <span>{isDeleting ? DELETING : CONFIRM_YES.toUpperCase()}</span>
                }}
            />
            <Container fluid className="d-flex flex-column pb-3 admin-document-viewer-container" style={{ height: 'calc(100vh - 50px)' }}>
                <div className="admin-document-breadcrumb-bar">
                    <Breadcrumb className="admin-document-breadcrumb">
                        {breadcrumbItems.map((crumb, index) => {
                            const isSelected = index === breadcrumbItems.length - 1;
                            const isAdminDocuments = index === ADMIN_DOCUMENT_BREADCRUMBS.length - 1;
                            return (
                                <BreadcrumbItem
                                    key={`${crumb.name}-${index}`}
                                    active={isSelected}
                                    className={isSelected || isAdminDocuments ? 'breadcrumb-item-bold' : ''}
                                >
                                    {crumb.node && !isSelected ? (
                                        <button
                                            type="button"
                                            className="admin-document-breadcrumb-link"
                                            onClick={() => handleTreeNodeClick(crumb.node!)}
                                        >
                                            {crumb.name}
                                        </button>
                                    ) : isAdminDocuments ? (
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            onClick={handleAdminDocumentsBreadcrumbClick}
                                            onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleAdminDocumentsBreadcrumbClick();
                                                }
                                            }}
                                        >
                                            {crumb.name}
                                        </span>
                                    ) : crumb.name}
                                </BreadcrumbItem>
                            );
                        })}
                    </Breadcrumb>
                    <div className="admin-document-search-box">
                        <FaSearch className="admin-document-search-icon" size={12} />
                        <TextInput
                            type="search"
                            size="sm"
                            className="admin-document-search-input"
                            placeholder={ADMIN_DOCUMENT_SEARCH_PLACEHOLDER}
                            value={searchTerm}
                            onChange={(e) => {
                                const sanitizedValue = sanitizeAllowedInput(e.currentTarget.value);
                                setSearchTerm(sanitizedValue);
                                if (!sanitizedValue.trim()) {
                                    setSearchResults(null);
                                }
                            }}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                        />
                    </div>
                </div>
                <Row className="flex-grow-1 overflow-hidden admin-document-content" style={{ flexWrap: 'nowrap' }}>
                        {/* ── Panel 1: Folder Tree ── */}
                        <Column lg={3} md={4} className="admin-doc-tree-panel">
                            <div className="admin-doc-tree-inner">
                                <div className="panel-title-bar">
                                    <div className="admin-doc-list-header-name">
                                        Administrative Documents
                                    </div>
                                </div>
                                <div style={{ borderBottom: '1px solid grey', flexShrink: 0 }}></div>
                                <div className="admin-doc-folder-filter-box">
                                    <FaSearch className="admin-doc-folder-filter-icon" size={12} />
                                    <TextInput
                                        type="search"
                                        size="sm"
                                        className="admin-doc-folder-filter-input"
                                        placeholder={FOLDER_FILTER_PLACEHOLDER}
                                        aria-label={FOLDER_FILTER_PLACEHOLDER}
                                        value={folderFilterText}
                                        onChange={(e) => {
                                            const sanitizedValue = sanitizeAllowedInput(e.currentTarget.value);
                                            if (!sanitizedValue.trim()) {
                                                clearFolderFilter();
                                                return;
                                            }
                                            setFolderFilterText(sanitizedValue);
                                        }}
                                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleFolderFilter();
                                            }
                                        }}
                                    />
                                </div>
                                <div className="tree-body">
                                    {renderTreeBody()}
                                </div>
                            </div>
                        </Column>

                        {/* ── Panels 2 & 3 are replaced by the search results view while a search is active ── */}
                        {(searchResults || isSearching) ? (
                            <Column lg={9} md={8} className="admin-doc-search-results-panel">
                                <div className="admin-doc-search-results-inner">
                                    <div className="panel-title-bar">
                                        <div className="admin-doc-list-header-name">
                                            {SEARCH_RESULTS_TITLE}
                                            <span className="admin-doc-search-total-count">{documentCount + folderCount}</span>
                                        </div>
                                        <Button
                                            outlined
                                            size="small"
                                            color="primary"
                                            onClick={clearSearch}
                                        >
                                            <FaTimes size={12} className="me-1" />
                                            {CLEAR_SEARCH}
                                        </Button>
                                    </div>
                                    <div style={{ borderBottom: '1px solid grey', flexShrink: 0 }}></div>
                                    <div className="admin-doc-search-summary-bar">
                                        <div className="admin-doc-search-summary-text">
                                            {SEARCH_RESULTS_FOR} <strong>"{activeSearchText}"</strong>
                                        </div>
                                        <div className="admin-doc-search-tabs">
                                            {([
                                                { key: 'all' as const, label: SEARCH_TAB_ALL, count: documentCount + folderCount },
                                                { key: 'documents' as const, label: SEARCH_DOCUMENTS_LABEL, count: documentCount },
                                                { key: 'folders' as const, label: SEARCH_FOLDERS_LABEL, count: folderCount }
                                            ]).map(tab => (
                                                <button
                                                    key={tab.key}
                                                    type="button"
                                                    disabled={tab.count === 0}
                                                    className={`admin-doc-search-tab${searchFilter === tab.key ? ' active' : ''}`}
                                                    onClick={() => setSearchFilter(tab.key)}
                                                >
                                                    {tab.label} {tab.count}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="admin-doc-search-results-body">
                                        {isSearching ? (
                                            <div className="empty-state">
                                                <p className="text-muted">{SEARCH_IN_PROGRESS}</p>
                                            </div>
                                        ) : folderCount === 0 && documentCount === 0 ? (
                                            <div className="empty-state">
                                                <p className="text-muted">{SEARCH_NO_RESULTS}</p>
                                            </div>
                                        ) : (
                                            <>
                                                {searchFilter !== 'folders' && documentCount > 0 && (
                                                    <div className="admin-doc-search-section">
                                                        <div className="admin-doc-search-section-title">
                                                            <FaFileAlt className="admin-doc-search-section-icon" />
                                                            {SEARCH_DOCUMENTS_LABEL}
                                                            <span className="admin-doc-search-section-count">{documentCount}</span>
                                                        </div>
                                                        <div className="admin-doc-search-section-scroll">
                                                            {searchResults?.document?.map(doc => (
                                                                <div
                                                                    key={doc.docFileId}
                                                                    className="admin-doc-search-row admin-doc-search-row-clickable"
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={() => handleSearchDocumentClick(doc)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            handleSearchDocumentClick(doc);
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className="admin-doc-search-row-ext">
                                                                        {(doc.fileExtension ?? '').replace('.', '').toUpperCase()}
                                                                    </div>
                                                                    <div className="admin-doc-search-row-text">
                                                                        <div className="admin-doc-search-row-name">{doc.name}</div>
                                                                        <div className="admin-doc-search-row-path">
                                                                            {getSearchDocumentFolderPath(doc.fullPath, doc.name)}
                                                                        </div>
                                                                    </div>
                                                                    {doc.documentTypeName && (
                                                                        <div className="admin-doc-search-row-type">{doc.documentTypeName}</div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {searchFilter !== 'documents' && folderCount > 0 && (
                                                    <div className="admin-doc-search-section">
                                                        <div className="admin-doc-search-section-title">
                                                            <FaRegFolder className="admin-doc-search-section-icon" />
                                                            {SEARCH_FOLDERS_LABEL}
                                                            <span className="admin-doc-search-section-count">{folderCount}</span>
                                                        </div>
                                                        <div className="admin-doc-search-section-scroll">
                                                            {searchResults?.folder?.map(folder => (
                                                                <div
                                                                    key={folder.id}
                                                                    className="admin-doc-search-row admin-doc-search-row-clickable"
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={() => handleSearchFolderClick(folder)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            handleSearchFolderClick(folder);
                                                                        }
                                                                    }}
                                                                >
                                                                    <FaRegFolder className="admin-doc-search-row-icon" />
                                                                    <div className="admin-doc-search-row-text">
                                                                        <div className="admin-doc-search-row-name">{folder.name}</div>
                                                                        <div className="admin-doc-search-row-path">{folder.fullPath}</div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Column>
                        ) : (
                            <>
                        {/* ── Panel 2: Document List ── */}
                        <Column lg={3} md={4} className="admin-doc-list-panel">
                            <div className="admin-doc-list-inner">
                                <div className="panel-title-bar">
                                    <div className="admin-doc-list-header-name">
                                        {selectedNode?.name || 'Documents'}
                                    </div>
                                    <Button
                                        size="small"
                                        color="primary"
                                        disabled={!selectedNode || !getIsUploadEnabled(selectedNode)}
                                        onClick={handleUploadButtonClick}
                                        title="Upload document"
                                    >
                                        <FaUpload size={12} className="me-1" />
                                        Upload
                                    </Button>
                                </div>
                                <div style={{ borderBottom: '1px solid grey', flexShrink: 0 }}></div>
                                <div className="admin-doc-list-filter-box">
                                    <FaSearch className="admin-doc-list-filter-icon" size={12} />
                                    <TextInput
                                        type="search"
                                        size="sm"
                                        className="admin-doc-list-filter-input"
                                        placeholder={DOCUMENT_FILTER_PLACEHOLDER}
                                        aria-label={DOCUMENT_FILTER_PLACEHOLDER}
                                        value={documentFilterText}
                                        onChange={e => {
                                            const sanitizedValue = sanitizeAllowedInput(e.currentTarget.value);
                                            setDocumentFilterText(sanitizedValue);
                                            if (!sanitizedValue.trim()) {
                                                setAppliedDocumentFilter('');
                                                documentGridRef.current?.gotoPage(0);
                                            }
                                        }}
                                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleDocumentFilter();
                                            }
                                        }}
                                    />
                                </div>

                                <div className="admin-doc-list-scroll">
                                    {!selectedNode ? (
                                        <div className="empty-state">
                                            <FaFileAlt size={32} className="text-muted mb-2" />
                                            <p className="text-muted">Select a folder to view its documents.</p>
                                        </div>
                                    ) : panelDocuments.length === 0 ? (
                                        <div className="empty-state">
                                            <p className="text-muted">No documents in this folder.</p>
                                        </div>
                                    ) : visiblePanelDocuments.length === 0 ? (
                                        <div className="empty-state">
                                            <p className="text-muted">{DOCUMENT_FILTER_NO_RESULTS}</p>
                                        </div>
                                    ) : (
                                        <div className="admin-doc-list-table-container">
                                            <Grid
                                                ref={documentGridRef}
                                                data={visiblePanelDocuments}
                                                showFilters={false}
                                                id={'adminDocumentListGrid'}
                                                retainGridState={true}
                                                columns={gridColumns}
                                                hiddenColumns={['selection']}
                                                sortDefault={[]}
                                                grdTableContainerClass="p-0 table-container-admin-doc"
                                                grdTabelClass="admin-doc-list-table grd-table table-striped table-hover table-sm"                                                
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Column>

                        {/* ── Panel 3: Document Viewer ── */}
                        {selectedDocumentId && (
                            <div className="admin-doc-viewer-panel">
                                {isPreviewLoading && (
                                    <div className="admin-doc-viewer-loading">
                                        <Spinner type="DotLoader" loading />
                                    </div>
                                )}
                                {selectedDocument && (
                                    <DocViewer
                                        document={selectedDocument.blob}
                                        scanId={selectedDocument.fileName}
                                        onLoadError={handlePreviewRenderError}
                                    />
                                )}
                                <div className="admin-doc-viewer-actions">
                                    <Button
                                        size="small"
                                        color="primary"
                                        title={ADD_VIEW_NOTES}
                                        onClick={handleOpenNotes}
                                    >
                                        {ADD_VIEW_NOTES}
                                    </Button>
                                </div>
                            </div>
                        )}
                            </>
                        )}
                    </Row>
            </Container>

            <Modal
                title={
                    (
                        <div className="admin-doc-notes-title">
                            <div>Notes History</div>
                            <div className="admin-doc-notes-document-name">
                                {selectedDocumentName || selectedDocument?.fileName || selectedDocumentId}
                            </div>
                        </div>
                    ) as unknown as string
                }
                show={showNotesModal}
                toggle={handleCloseNotes}
                verticallyCentered={true}
                className="admin-doc-notes-modal"
                buttonsAlignment="right"
                body={
                    <div className="admin-doc-notes-modal-body">
                        <div className="admin-doc-notes-history" aria-live="polite">
                            {notesLoadError ? (
                                <p className="text-danger" role="alert">Failed to load notes.</p>
                            ) : documentNotes.length === 0 ? (
                                <p className="text-muted">No notes available.</p>
                            ) : (
                                documentNotes.map(note => (
                                    <div key={note.id} className="admin-doc-note-entry">
                                        <div className="admin-doc-note-meta">
                                            <div>
                                                <strong>{note.addedBy}</strong>
                                                <span className="admin-doc-note-label">Note</span>
                                            </div>
                                            <strong>{note.enteredDate ? moment(note.enteredDate).format('MM/DD/YYYY hh:mm A') : ''}</strong>
                                        </div>
                                        <div className="admin-doc-note-text">{note.notes}</div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="admin-doc-note-compose">
                            <input
                                type="text"
                                id="admin-document-new-note"
                                aria-label={NEW_NOTE_LABEL}
                                placeholder={NEW_NOTE_PLACEHOLDER}
                                className="form-control admin-doc-new-note"
                                maxLength={250}
                                value={newDocumentNote}
                                disabled={isSavingNote}
                                onChange={e => setNewDocumentNote(e.currentTarget.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        void handleSubmitNote();
                                    }
                                }}
                            />
                            <Button
                                size="small"
                                color="primary"
                                disabled={isSavingNote || notesLoadError || !newDocumentNote.trim()}
                                onClick={handleSubmitNote}
                            >
                                {SUBMIT}
                            </Button>
                        </div>
                    </div>
                }
            />

            <Modal
                verticallyCentered={true}
                className="admin-doc-delete-folder-modal"
                title={DELETE_FOLDER_TITLE}
                show={showDeleteFolderModal}
                toggle={handleDeleteFolderCancel}
                buttonsAlignment="right"
                body={
                    <div>
                        <p className="mb-2">{DELETE_FOLDER_PROMPT} <strong>{folderPendingDelete?.name ?? ''}</strong>?</p>
                        {folderDocumentCount > 0 && (
                            <>
                                <p className="mb-2 text-nowrap">
                                    <strong>{DELETE_FOLDER_NOT_EMPTY}</strong> {DELETE_FOLDER_ALSO_REMOVES} <strong>{folderDocumentCount} {DELETE_FOLDER_DOCUMENTS_LABEL}</strong>.
                                </p>
                                <div className="form-check admin-doc-delete-folder-confirm mb-4">
                                    <input
                                        id="adminDocDeleteFolderConfirm"
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={deleteFolderConfirmed}
                                        onChange={(e) => setDeleteFolderConfirmed(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="adminDocDeleteFolderConfirm">
                                        {DELETE_FOLDER_CONFIRM_CHECKBOX}
                                    </label>
                                </div>
                            </>
                        )}
                        <p className="text-muted mb-3">{DELETE_FOLDER_WARNING}</p>
                        <Row>
                            <Column lg={12} md={12} className="d-flex justify-content-end gap-2">
                                <Button
                                    outlined
                                    size="small"
                                    color="primary"
                                    disabled={isDeletingFolder}
                                    onClick={handleDeleteFolderCancel}
                                >
                                    {CANCEL}
                                </Button>
                                <Button
                                    size="small"
                                    color="primary"
                                    disabled={isDeletingFolder || (folderDocumentCount > 0 && !deleteFolderConfirmed)}
                                    onClick={handleDeleteFolderConfirm}
                                >
                                    {isDeletingFolder ? DELETING : ROW_ACTION_DELETE}
                                </Button>
                            </Column>
                        </Row>
                    </div>
                }
            />

            {/* Folder Creation Modal */}
            <Modal
                verticallyCentered={true}
                className="admin-doc-create-folder-modal"
                title={
                    (
                        <div className="admin-doc-create-folder-title">
                            <div className="admin-doc-create-folder-title-main">{ADD_SUB_FOLDER}</div>
                            {createFolderParentNode && (
                                <div className="admin-doc-create-folder-subtitle">
                                    {findPathToNode(treeNodes, createFolderParentNode.id)?.join(' / ')}
                                </div>
                            )}
                        </div>
                    ) as unknown as string
                }
                show={showCreateFolderModal}
                toggle={() => { setShowCreateFolderModal(false); resetCreateFolderModal(); }}
                buttonsAlignment="right"
                body={
                    <div className="admin-doc-create-folder-modal-body">
                        <Row className="mb-3">
                            <Column lg={12} md={12}>
                                <label className="form-label">
                                    {FOLDER_NAME} <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${(createFolderValidation.name || createFolderValidation.exists || createFolderValidation.tooShort || createFolderValidation.tooLong || createFolderValidation.forbiddenChars || createFolderValidation.trailingChars) ? 'is-invalid' : ''}`}
                                    placeholder={FOLDER_NAME_PLACEHOLDER}
                                    value={createFolderName}
                                    maxLength={FOLDER_NAME_MAX_LENGTH}
                                    onChange={(e) => {
                                        setCreateFolderName(sanitizeAllowedInput(e.target.value));
                                        setCreateFolderValidation({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleCreateFolderSubmit();
                                        }
                                    }}
                                    autoFocus
                                />
                                {createFolderValidation.name && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_REQUIRED}</div>
                                )}
                                {createFolderValidation.exists && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_EXISTS}</div>
                                )}
                                {createFolderValidation.tooShort && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_TOO_SHORT}</div>
                                )}
                                {createFolderValidation.tooLong && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_TOO_LONG}</div>
                                )}
                                {createFolderValidation.forbiddenChars && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_FORBIDDEN_CHARACTERS_MESSAGE}</div>
                                )}
                                {createFolderValidation.trailingChars && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_TRAILING_CHARACTERS_MESSAGE}</div>
                                )}
                            </Column>
                        </Row>
                        <Row>
                            <Column lg={12} md={12} className="d-flex justify-content-end gap-2">
                                <Button
                                    outlined
                                    size="small"
                                    color="primary"
                                    onClick={() => { setShowCreateFolderModal(false); resetCreateFolderModal(); }}
                                >
                                    {CANCEL}
                                </Button>
                                <Button
                                    size="small"
                                    color="primary"
                                    disabled={!createFolderName.trim()}
                                    onClick={handleCreateFolderSubmit}
                                >
                                    {CREATE}
                                </Button>
                            </Column>
                        </Row>
                    </div>
                }
            />

            {/* Folder Rename Modal */}
            <Modal
                verticallyCentered={true}
                className="admin-doc-create-folder-modal"
                title={
                    (
                        <div className="admin-doc-create-folder-title">
                            <div className="admin-doc-create-folder-title-main">{RENAME_SUB_FOLDER}</div>
                            {renameFolderNode && (
                                <div className="admin-doc-create-folder-subtitle">
                                    {findPathToNode(treeNodes, renameFolderNode.id)?.slice(0, -1).join(' / ')}
                                </div>
                            )}
                        </div>
                    ) as unknown as string
                }
                show={showRenameFolderModal}
                toggle={() => { setShowRenameFolderModal(false); resetRenameFolderModal(); }}
                buttonsAlignment="right"
                body={
                    <div className="admin-doc-create-folder-modal-body">
                        <Row className="mb-3">
                            <Column lg={12} md={12}>
                                <label className="form-label">{CURRENT_NAME}</label>
                                <div className="admin-doc-rename-current-name">
                                    <FaRegFolder size={14} className="me-2" />
                                    {renameFolderNode?.name}
                                </div>
                            </Column>
                        </Row>
                        <Row className="mb-3">
                            <Column lg={12} md={12}>
                                <label className="form-label">
                                    {NEW_NAME} <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${(renameFolderValidation.name || renameFolderValidation.exists || renameFolderValidation.tooShort || renameFolderValidation.tooLong || renameFolderValidation.forbiddenChars || renameFolderValidation.trailingChars) ? 'is-invalid' : ''}`}
                                    placeholder={FOLDER_NAME_PLACEHOLDER}
                                    value={renameFolderName}
                                    maxLength={FOLDER_NAME_MAX_LENGTH}
                                    onChange={(e) => {
                                        setRenameFolderName(sanitizeAllowedInput(e.target.value));
                                        setRenameFolderValidation({ name: false, exists: false, tooShort: false, tooLong: false, forbiddenChars: false, trailingChars: false });
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleRenameFolderSubmit();
                                        }
                                    }}
                                    autoFocus
                                />
                                {renameFolderValidation.name && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_REQUIRED}</div>
                                )}
                                {renameFolderValidation.exists && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_EXISTS}</div>
                                )}
                                {renameFolderValidation.tooShort && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_TOO_SHORT}</div>
                                )}
                                {renameFolderValidation.tooLong && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_TOO_LONG}</div>
                                )}
                                {renameFolderValidation.forbiddenChars && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_FORBIDDEN_CHARACTERS_MESSAGE}</div>
                                )}
                                {renameFolderValidation.trailingChars && (
                                    <div className="invalid-feedback d-block">{FOLDER_NAME_TRAILING_CHARACTERS_MESSAGE}</div>
                                )}
                            </Column>
                        </Row>
                        <Row>
                            <Column lg={12} md={12} className="d-flex justify-content-end gap-2">
                                <Button
                                    outlined
                                    size="small"
                                    color="primary"
                                    onClick={() => { setShowRenameFolderModal(false); resetRenameFolderModal(); }}
                                >
                                    {CANCEL}
                                </Button>
                                <Button
                                    size="small"
                                    color="primary"
                                    disabled={!renameFolderName.trim() || renameFolderName.trim().toLowerCase() === renameFolderNode?.name.toLowerCase()}
                                    onClick={handleRenameFolderSubmit}
                                >
                                    {SAVE}
                                </Button>
                            </Column>
                        </Row>
                    </div>
                }
            />

            {/* ── Upload Modal ── */}
            <Modal
                verticallyCentered={true}
                title={selectedNode ? `${UPLOAD_FILES_TO} ${selectedNode.name}` : UPLOAD_DOCUMENT}
                show={showUploadModal}
                toggle={() => { setShowUploadModal(false); resetUploadModal(); }}
                buttonsAlignment="right"
                body={
                    <div className="admin-doc-upload-modal-body">
                        {/* Folder path */}
                        <Row className="mb-3">
                            <Column lg={12} md={12}>
                                <label className="admin-doc-upload-label">{DESTINATION}</label>
                                <div className="admin-doc-upload-folder-path">
                                    {selectedNodePath}
                                </div>
                            </Column>
                        </Row>

                        {/* Document Type */}
                        <Row className="mb-3">
                            <Column lg={12} md={12}>
                                <Select
                                    id="adminDocUploadDocType"
                                    controlId="adminDocUploadDocType"
                                    label={DOCUMENT_TYPE}
                                    isRequired={true}
                                    className={uploadValidation.documentType ? 'cust-doc-input-error' : ''}
                                    options={uploadDocumentTypes}
                                    onChange={(val) => {
                                        setUploadDocumentTypeId(val?.toString() ?? '');
                                        setUploadValidation(v => ({ ...v, documentType: false }));
                                    }}
                                    defaultSelected={uploadDocumentTypes.filter(({ value }) => value === uploadDocumentTypeId)}
                                    placeholder="Choose Type..."
                                    multiple={false}
                                />
                                {uploadValidation.documentType && (
                                    <div className="admin-doc-upload-error">{DOCUMENT_TYPE_REQUIRED}</div>
                                )}
                            </Column>
                        </Row>

                        {/* Drop zone */}
                        <Row className="mb-2">
                            <Column lg={12} md={12}>
                                <div
                                    className={`admin-doc-dropzone${uploadDragOver ? ' drag-over' : ''}${uploadValidation.files ? ' has-error' : ''}`}
                                    onClick={() => uploadFileInputRef.current?.click()}
                                    onDragOver={e => { e.preventDefault(); setUploadDragOver(true); }}
                                    onDragLeave={() => setUploadDragOver(false)}
                                    onDrop={e => {
                                        e.preventDefault();
                                        setUploadDragOver(false);
                                        handleUploadFilesAdd(e.dataTransfer.files);
                                    }}
                                >
                                    <input
                                        ref={uploadFileInputRef}
                                        type="file"
                                        accept={UPLOAD_ACCEPT_ATTRIBUTE}
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={e => {
                                            const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
                                            handleUploadFilesAdd(selectedFiles);
                                            // Reset input so selecting the same file again still triggers onChange.
                                            e.target.value = '';
                                        }}
                                    />
                                    <FaCloudUploadAlt size={28} className="admin-doc-dropzone-icon" />
                                    <span className="admin-doc-dropzone-text">
                                        Drag &amp; drop files here, or <span className="admin-doc-dropzone-browse">browse</span>
                                    </span>
                                </div>
                                {uploadValidation.files && (
                                    <div className="admin-doc-upload-error">Please select at least one file.</div>
                                )}
                                <div className="admin-doc-upload-warning-message mt-2">
                                    {ALLOWED_UPLOAD_FILE_TYPES_LABEL}
                                </div>
                            </Column>
                        </Row>

                        {/* Selected file list */}
                        {uploadFiles.length > 0 && (
                            <Row className="mb-3">
                                <Column lg={12} md={12}>
                                    <div className="admin-doc-upload-file-table-wrapper">
                                        <table className="admin-doc-upload-file-table">
                                            <thead>
                                                <tr>
                                                    <th className="col-ext">Type</th>
                                                    <th className="col-name">File Name</th>
                                                    <th className="col-size">Size</th>
                                                    <th className="col-remove"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {uploadFiles.map((file, idx) => {
                                                    const ext = file.name.includes('.')
                                                        ? file.name.split('.').pop()?.toUpperCase()
                                                        : '—';
                                                    const sizeKb = file.size / 1024;
                                                    const sizeDisplay = sizeKb >= 1024
                                                        ? `${(sizeKb / 1024).toFixed(1)} MB`
                                                        : `${sizeKb.toFixed(1)} KB`;
                                                    return (
                                                        <tr key={idx}>
                                                            <td className="col-ext">
                                                                <span className="admin-doc-upload-ext-badge">{ext}</span>
                                                            </td>
                                                            <td className="col-name" title={file.name}>{file.name}</td>
                                                            <td className="col-size">{sizeDisplay}</td>
                                                            <td className="col-remove">
                                                                <button
                                                                    type="button"
                                                                    className="admin-doc-upload-file-remove"
                                                                    aria-label={`Remove ${file.name}`}
                                                                    onClick={() => handleUploadFileRemove(idx)}
                                                                >
                                                                    <FaTimes size={11} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </Column>
                            </Row>
                        )}

                        {/* Actions */}
                        <Row>
                            <Column lg={12} md={12} className="d-flex justify-content-end gap-2">
                                <Button
                                    outlined
                                    size="small"
                                    color="primary"
                                    disabled={isUploading}
                                    onClick={() => { setShowUploadModal(false); resetUploadModal(); }}
                                >
                                    {CANCEL}
                                </Button>
                                <Button
                                    size="small"
                                    color="primary"
                                    disabled={isUploading || !uploadDocumentTypeId || uploadFiles.length === 0}
                                    onClick={handleUploadSubmit}
                                >
                                    <FaUpload size={12} className="me-1" />
                                    {isUploading ? `Uploading... (${uploadFiles.length})` : `Upload${uploadFiles.length > 0 ? ` (${uploadFiles.length})` : ''}`}
                                </Button>
                            </Column>
                        </Row>
                    </div>
                }
            />
            <BulkProgressModal
                show={uploadProgress.isProcessing || uploadProgress.isComplete}
                title={UPLOAD_DOCUMENT}
                processingTitle={UPLOAD_PROCESSING_TITLE}
                completeTitle={UPLOAD_COMPLETE_TITLE}
                processingMessage={UPLOAD_PROCESSING_MESSAGE}
                current={uploadProgress.current}
                total={uploadProgress.total}
                successCount={uploadProgress.successCount}
                failureCount={uploadProgress.failureCount}
                successFileNames={uploadProgress.successFileNames}
                failureFileNames={uploadProgress.failureFileNames}
                isProcessing={uploadProgress.isProcessing}
                isComplete={uploadProgress.isComplete}
                onComplete={handleUploadProgressComplete}
            />
        </>
    );
};

export default AdminDocumentViewer;
